import { ChangeEvent, useContext, useEffect, useState, useMemo } from 'react';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { LocationAndTeamsOption } from '@vyce/core/src/types';
import { useBooleanState } from '@vyce/core/src/hooks';
import {
  getTeamsAndLocationsOptionsRequest,
  updateTeamsAndLocationsOptionsRequest,
  resetTeamsAndLocationsOptionsRequest,
} from '@vyce/core/src/api/dashboards';

import { CandidateToUpdate, Props } from '../types';

export const useData = ({
  widgetItems,
  companyId,
  teamPermission,
  timePermission,
  mainDashboardFiltersFromState,
  updateMainDashboardFilters,
  onWidgetsChangeViaSettings,
  resetDashboards,
}: Props) => {
  const [open, setOpenDialog, setCloseDialog] = useBooleanState(false);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [accordionName, setAccordionName] = useState<'windows' | 'locations' | 'teams' | null>(null);
  const [locationList, setLocationList] = useState<LocationAndTeamsOption[]>([]);
  const [initialLocationListBeforeCommit, setInitialLocationListBeforeCommit] = useState<
    LocationAndTeamsOption[]
  >([]);
  const [teamList, setTeamList] = useState<LocationAndTeamsOption[]>([]);
  const [initialTeamListBeforeCommit, setInitialTeamListBeforeCommit] = useState<LocationAndTeamsOption[]>(
    []
  );
  const [candidatesToUpdate, setCandidatesToUpdate] = useState<CandidateToUpdate[]>([]);
  const [isOpenConfirmDialog, setConfirmDialogOpen, setConfirmDialogClose] = useBooleanState(false);
  const { isMobile } = useContext(DeviceContext);

  const selectedLocationsCount = useMemo(
    () => locationList.filter(item => item.is_active).length,
    [locationList]
  );
  const selectedTeamsCount = useMemo(() => teamList.filter(item => item.is_active).length, [teamList]);

  const notAllTeamsSelected = useMemo(
    () => teamPermission && teamList.length > 0 && selectedTeamsCount < teamList.length,
    [teamPermission, teamList.length, selectedTeamsCount]
  );

  const allTeamsSelected = useMemo(
    () => teamList.length > 0 && selectedTeamsCount === teamList.length,
    [teamList.length, selectedTeamsCount]
  );

  const notAllLocationsSelected = useMemo(
    () => timePermission && locationList.length > 0 && selectedLocationsCount < locationList.length,
    [timePermission, locationList.length, selectedLocationsCount]
  );

  const allLocationsSelected = useMemo(
    () => locationList.length > 0 && selectedLocationsCount === locationList.length,
    [locationList.length, selectedLocationsCount]
  );

  const showRestoreButton = useMemo(
    () =>
      teamList.some(item => !item.is_active) ||
      locationList.some(item => !item.is_active) ||
      initialLocationListBeforeCommit.some(item => !item.is_active) ||
      initialTeamListBeforeCommit.some(item => !item.is_active) ||
      candidatesToUpdate.some(item => !item.is_active) ||
      widgetItems.some(item => !item.is_active),
    [
      teamList,
      locationList,
      candidatesToUpdate,
      initialLocationListBeforeCommit,
      initialTeamListBeforeCommit,
      widgetItems,
    ]
  );

  const onCandidateToUpdateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const systemName = event.target.dataset.systemName ?? '';
    if (candidatesToUpdate?.length === 0 || !systemName) return;

    setCandidatesToUpdate(value => {
      return (
        value?.map(item => {
          return item.system_name === systemName ? { ...item, is_active: !item.is_active } : item;
        }) || []
      );
    });
  };

  const fetchTeamsAndLocationsOptions = async () => {
    if (mainDashboardFiltersFromState?.locations.length || mainDashboardFiltersFromState?.teams.length) {
      setLocationList(mainDashboardFiltersFromState.locations);
      setInitialLocationListBeforeCommit(mainDashboardFiltersFromState.locations);
      setTeamList(mainDashboardFiltersFromState.teams);
      setInitialTeamListBeforeCommit(mainDashboardFiltersFromState.teams);
      return;
    }
    try {
      const res = await getTeamsAndLocationsOptionsRequest(companyId);
      setLocationList(res.data.locations);
      setInitialLocationListBeforeCommit(res.data.locations);
      setTeamList(res.data.teams);
      setInitialTeamListBeforeCommit(res.data.teams);
      handleUpdateMainDashboardFilters({ locations: res.data.locations, teams: res.data.teams });
    } catch (e) {
      console.error(e);
    } finally {
      reorderLists();
    }
  };

  const handleUpdateMainDashboardFilters = ({
    locations,
    teams,
  }: {
    locations?: LocationAndTeamsOption[];
    teams?: LocationAndTeamsOption[];
  }) => {
    // update redux store
    updateMainDashboardFilters?.({
      locations: locations || locationList,
      teams: teams || teamList,
    });
  };

  const updateWidgetsSettings = async () => {
    const mappedCandidatesToUpdate = candidatesToUpdate.reduce((sum, curr) => {
      sum[curr.system_name] = curr;
      return sum;
    }, {} as Record<string, CandidateToUpdate>);
    const newWidgetsSettings = widgetItems.map(item => ({
      ...item,
      is_active: mappedCandidatesToUpdate[item.system_name]?.is_active ?? item.is_active,
    }));
    await onWidgetsChangeViaSettings(newWidgetsSettings);
  };

  const updateTeamsAndLocationsOptions = async () => {
    try {
      await updateTeamsAndLocationsOptionsRequest({
        companyId,
        payload: {
          locations: locationList,
          teams: teamList,
        },
      });
      setInitialLocationListBeforeCommit(locationList);
      setInitialTeamListBeforeCommit(teamList);
      reorderLists();
      handleUpdateMainDashboardFilters({});
    } catch (e) {
      console.error(e);
    }
  };

  const resetTeamsAndLocationsOptions = async () => {
    try {
      const res = await resetTeamsAndLocationsOptionsRequest(companyId);
      setLocationList(res.data.locations);
      setTeamList(res.data.teams);
      setInitialLocationListBeforeCommit(res.data.locations);
      setInitialTeamListBeforeCommit(res.data.teams);
      handleUpdateMainDashboardFilters({ locations: res.data.locations, teams: res.data.teams });
    } catch (e) {
      console.error(e);
    }
  };

  const handleResetClick = async () => {
    resetTeamsAndLocationsOptions();
    resetDashboards();
    setConfirmDialogClose();
  };

  const handleLocationChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocationList(list =>
      list.map(item => {
        return item.uuid === event.target.dataset.systemId ? { ...item, is_active: !item.is_active } : item;
      })
    );
  };

  const handleSelectAllLocationsChange = () => {
    const nextActive = !allLocationsSelected;
    setLocationList(list => list.map(item => ({ ...item, is_active: nextActive })));
  };

  const handleTeamChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTeamList(list =>
      list.map(item => {
        return item.uuid === event.target.dataset.systemId ? { ...item, is_active: !item.is_active } : item;
      })
    );
  };

  const handleSelectAllTeamsChange = () => {
    const nextActive = !allTeamsSelected;
    setTeamList(list => list.map(item => ({ ...item, is_active: nextActive })));
  };

  const reorderLists = () => {
    setLocationList(list => {
      return list.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
    });

    setTeamList(list => {
      return list.sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
    });
  };

  const handleUpdateClick = async () => {
    setLoadingTrue();
    await Promise.all([updateTeamsAndLocationsOptions(), updateWidgetsSettings()])
      .then(() => {
        setCloseDialog();
      })
      .finally(() => {
        setLoadingFalse();
      });
  };

  const handleClose = () => {
    setCloseDialog();
    setAccordionName(null);
  };

  const handleAccordionChange = (panel: 'windows' | 'locations' | 'teams') => {
    setAccordionName(panel === accordionName ? null : panel);
  };

  useEffect(() => {
    fetchTeamsAndLocationsOptions();
  }, [companyId]);

  useEffect(() => {
    setCandidatesToUpdate(
      widgetItems.map(item => ({
        name: item.name,
        is_active: item.is_active ?? false,
        system_name: item.system_name,
      }))
    );
  }, [widgetItems]);

  return {
    isMobile,
    open,
    showRestoreButton,
    accordionName,
    candidatesToUpdate,
    selectedLocationsCount,
    notAllLocationsSelected,
    allLocationsSelected,
    locationList,
    selectedTeamsCount,
    notAllTeamsSelected,
    allTeamsSelected,
    teamList,
    loading,
    isOpenConfirmDialog,
    setConfirmDialogOpen,
    setConfirmDialogClose,
    handleTeamChange,
    handleSelectAllTeamsChange,
    handleUpdateClick,
    handleLocationChange,
    handleSelectAllLocationsChange,
    onCandidateToUpdateChange,
    handleAccordionChange,
    handleResetClick,
    handleClose,
    setOpenDialog,
  };
};
